import type { OemId } from './types.js';

/**
 * SDK-free OEM detection.
 *
 * Identifies the device's hardware vendor from Android system properties
 * (`ro.product.manufacturer` / `brand` / `model`) with a User-Agent
 * fallback — NO vendor SDK (Zebra MX, Samsung Knox, Honeywell EZConfig)
 * required. This is the half of OEM support that can be built and tested
 * without the physical device or its SDK; the per-OEM modules use it as
 * the cheap first gate before any SDK probe.
 *
 * Safety: callers (the orchestrator) only run detection for entitled
 * OEMs, so a model-prefix heuristic matching the "wrong" vendor can't
 * activate an OEM the fleet isn't licensed for. Manufacturer/brand is the
 * authoritative signal; model prefixes are a secondary pass for devices
 * that report a generic manufacturer.
 */

/** Injected access to device identity. Both members optional/tolerant so
 *  this stays decoupled from the renderer's AgentHost. */
export interface OemDetectionProbe {
  /** Read an Android system property; resolves undefined when unavailable. */
  getSystemProperty?: (name: string) => Promise<string | undefined>;
  /** Browser/WebView User-Agent, used as a last-resort signal. */
  userAgent?: string;
}

/** Order OEMs are considered in (matches the orchestrator's canonical order). */
const DETECTION_ORDER: readonly OemId[] = ['zebra', 'samsung', 'honeywell'];

/** Authoritative manufacturer/brand substrings (lowercased). */
const VENDOR_KEYWORDS: Readonly<Record<OemId, readonly string[]>> = {
  // Zebra TC/MC-series often report "Zebra Technologies"; legacy units
  // and some firmware still report the Motorola Solutions lineage.
  zebra: ['zebra', 'motorola solutions'],
  samsung: ['samsung'],
  honeywell: ['honeywell'],
};

/** Distinctive model prefixes (lowercased), used only when the
 *  manufacturer/brand is generic or missing. */
const MODEL_PREFIXES: Readonly<Record<OemId, readonly string[]>> = {
  // Zebra rugged lines: TC, MC, ET4x/ET5x, EC, WT, CC, PS20.
  zebra: ['tc', 'mc', 'et4', 'et5', 'ec', 'wt', 'cc', 'ps2'],
  // Samsung models are SM-prefixed (Galaxy / XCover / Tab Active).
  samsung: ['sm-'],
  // Honeywell rugged lines: CT, EDA, CN, CK, ScanPal/Dolphin families.
  honeywell: ['ct', 'eda', 'cn', 'ck', 'scanpal', 'dolphin'],
};

const SYSTEM_PROPS = {
  manufacturer: 'ro.product.manufacturer',
  brand: 'ro.product.brand',
  model: 'ro.product.model',
} as const;

async function readProp(probe: OemDetectionProbe, name: string): Promise<string> {
  if (probe.getSystemProperty === undefined) return '';
  try {
    return (await probe.getSystemProperty(name))?.toLowerCase() ?? '';
  } catch {
    return '';
  }
}

/**
 * Best-effort: returns the detected OEM, or null when the device doesn't
 * match a known vendor (in which case the launcher runs stock).
 */
export async function detectOem(probe: OemDetectionProbe): Promise<OemId | null> {
  const [manufacturer, brand, model] = await Promise.all([
    readProp(probe, SYSTEM_PROPS.manufacturer),
    readProp(probe, SYSTEM_PROPS.brand),
    readProp(probe, SYSTEM_PROPS.model),
  ]);
  const ua = (probe.userAgent ?? '').toLowerCase();

  // Pass 1 — authoritative manufacturer/brand (also checked in the UA,
  // since some Zebra/Honeywell WebViews inject the vendor name there).
  const vendorHaystack = `${manufacturer} ${brand} ${ua}`;
  for (const oem of DETECTION_ORDER) {
    if (VENDOR_KEYWORDS[oem].some((kw) => vendorHaystack.includes(kw))) {
      return oem;
    }
  }

  // Pass 2 — distinctive model prefix, for devices reporting a generic
  // manufacturer (e.g. an ODM string). Entitlement gating upstream keeps
  // this from mis-activating an unlicensed OEM.
  for (const oem of DETECTION_ORDER) {
    if (MODEL_PREFIXES[oem].some((prefix) => model.startsWith(prefix))) {
      return oem;
    }
  }

  return null;
}
